import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { isEmpty } from 'lodash';
// import Badge from 'components/v2/Badge';
import { StatsContainer, MetricCard } from 'components/v2/stats';
import Spinner from 'components/ui/Spinner';
import {
  getShipperPerformanceMetrics,
  getCargomaticPerformanceMetrics,
} from 'utils/MetricAPIUtils';
import { handleDataStream, convertJsonToCSV } from 'utils/StreamHandlingUtils';
import SessionStore from 'stores/SessionStore';
import { findUsers } from 'utils/UserWebAPIUtils';
import FetchWebAPIUtils from 'utils/FetchWebAPIUtils';
import CalendarIcon from '../../v2/icons/CalendarIcon';
import MetricsList from '../../v2/metric-list';
import Container from '../../v2/container';

const whitelist = [
  'appUsage',
  'completionRate',
  'overallScore',
  'overallStars',
  'onTimePercentage',
  'stopCompletionTimeliness',
  'podQuality',
  'podTimeliness',
];

const initialStats = {
  appUsage: 0,
  completionRate: 0,
  overallScore: 0,
  overallStars: 0,
  onTimePercentage: 0,
  stopCompletionTimeliness: 0,
  podQuality: 0,
  podTimeliness: 0,
};

const weeks = {
  ONE: 1,
  TWO: 2,
  SIX: 6,
};

const SHIPMENT_TYPES = [
  {
    name: 'All',
    value: '',
  },
  {
    name: 'Shorthaul',
    value: 'shorthaul',
  },
  {
    name: 'Drayage',
    value: 'drayage',
  },
];

const TIME_FILTERS = [
  {
    name: 'This week',
    value: weeks.ONE,
  },
  {
    name: '2 weeks ago',
    value: weeks.TWO,
  },
  {
    name: '6 weeks ago',
    value: weeks.SIX,
  },
];

/*
const dummyData = [
  {
    _id: '61326c0a1b43be00b4bf8316',
    _type: 'Cargomatic',
    account: null,
    shipper: null,
    carrier: null,
    shipmentType: 'drayage',
    timestamp: '2021-09-09T00:20:11.565Z',
    datetime: '2120210909',
    datestring: 'six_weeks_ago',
    overallScore: 1182.1137026077504,
    onTimePercentage: 7.748538011695906,
    podTimeliness: 25.313995215311007,
    stopCompletionTimeliness: 25.438596491228072,
    appUsage: 8.698830409356725,
    podQuality: 20.492404285230133,
    completionRate: 7.0175438596491215,
    __v: 0,
    overallStars: 1.2249685648820479,
  },
  {
    _id: '6137bb65dc690b001f8a382b',
    _type: 'Cargomatic',
    account: null,
    shipper: null,
    carrier: null,
    shipmentType: 'shorthaul',
    timestamp: '2021-09-09T00:20:11.565Z',
    datetime: '2120210909',
    datestring: 'six_weeks_ago',
    overallScore: 1319.5194214120172,
    onTimePercentage: 12.729498164014688,
    podTimeliness: 15.789473684210526,
    stopCompletionTimeliness: 19.599143206854347,
    appUsage: 17.472460220318236,
    podQuality: 17.814855237896932,
    completionRate: 17.96338672768879,
    __v: 0,
    overallStars: 1.0189520468037165,
  },
];
*/

const CargomaticMetricsDashboard = ({ id }) => {
  const [timeAgo, setTimeAgo] = useState(weeks.SIX);
  const [stats, setStats] = useState([]);
  const [shipper, setShipper] = useState([]);
  const [displayShipperMetrics] = useState(!id);
  const [shipmentType, setShipmentType] = useState('');
  const [isExporting, setExporting] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  let abortController;

  const getMetrics = async () => {
    let response;
    const timeFilters = getSimpleFilters();
    let query = {
      ...timeFilters,
    };

    if (shipmentType) {
      query.shipmentType = shipmentType;
    }

    if (id) {
      query = { ...query, shipper: id };
      response = await getShipperPerformanceMetrics(query);
      const averages = getAverageShipperScores(response);
      setStats(getStatConfigs(id, averages, timeAgo, shipmentType));

      // Get the shipper name
      const results = await findUsers({ _id: id });
      const item = results.entity.items[0];
      setShipper(item);
    } else {
      query = { ...query, type: 'Cargomatic' };
      response = await getCargomaticPerformanceMetrics(query);
      const averages = getAverageShipperScores(response);
      setStats(getStatConfigs(id, averages, timeAgo, shipmentType));
    }
  };

  useEffect(() => {
    getMetrics();
  }, [id, timeAgo, shipmentType]);

  function handleSelectTime(e) {
    setTimeAgo(parseInt(e.target.value, 10));
  }

  function handleShipmentTypeSelect(e) {
    const {
      target: { value },
    } = e;
    setShipmentType(value);
  }

  const getAverageShipperScores = (data) => {
    const count = data.length;

    const totals = data.reduce(
      (summary, statItem) => {
        whitelist.forEach((key) => {
          summary[key] += statItem[key];
        });
        return summary;
      },
      {
        ...initialStats,
      },
    );

    return whitelist.reduce((finalSummary, key) => {
      finalSummary[key] = totals[key] / count;
      return finalSummary;
    }, {});
  };

  const getSimpleFilters = () => {
    let time;
    switch (timeAgo) {
      case 2:
        time = 'two_weeks_ago';
        break;
      case 6:
        time = 'six_weeks_ago';
        break;
      default:
        time = 'this_week';
        break;
    }
    return {
      datestring: time,
      timestamp: moment().startOf('day').toISOString(),
    };
  };

  const exportCSV = async () => {
    const type = id && !isEmpty(shipper) ? 'shipper' : 'internal';
    const payload = { date: timeAgo, type, shipmentType };
    if (type === 'shipper') {
      payload.account = id;
    }
    if (abortController) {
      await abortController();
    }
    setExporting(true);
    const totalRowCountResponse =
      await FetchWebAPIUtils.getMetricScorecardCSVExportRowCount(payload);
    setTotalRowCount(totalRowCountResponse.total);
    const { response, controller } =
      await FetchWebAPIUtils.getMetricScorecardCSVExport(payload);
    abortController = controller;
    await handleDataStream(response, handleStreamEnd);
  };

  const handleStreamEnd = (rows) => {
    setExporting(false);
    const filename =
      shipper && shipper.account_name
        ? `${shipper.account_name}_${
            shipmentType || 'all'
          }_scorecard_${moment().format('L')}.csv`
        : `internal_${shipmentType}_scorecard.csv`;
    return convertJsonToCSV(rows, filename);
  };

  /*
  const getBadge = () => {
    const badgeColor = !displayShipperMetrics ? 'green' : 'red';
    const badgeLabel = !displayShipperMetrics ? 'Shipper' : 'Internal';
    return (
      <Badge color={badgeColor} size="large" rounded>
        {badgeLabel}
      </Badge>
    );
  };
  */
  return (
    <Container className="py-8">
      <div className="flex justify-between">
        <span>
          {!displayShipperMetrics && (
            <h2 className="font-medium my-0 mb-4">{shipper.account_name}</h2>
          )}
          <h3 className="my-0 mb-4">
            {displayShipperMetrics
              ? 'Cargomatic Overall Performance'
              : 'Shipper Performance'}
          </h3>
        </span>
        <span>
          {SessionStore.isMetricsAdminUser() && (
            <span className="flex">
              {isExporting && (
                <p className="pr-2 my-auto">{`Retrieving ${totalRowCount} rows`}</p>
              )}
              <button
                className="button button-green"
                onClick={exportCSV}
                disabled={isExporting}
              >
                {isExporting ? <Spinner message="Exporting..." /> : 'Export'}
              </button>
            </span>
          )}
        </span>
      </div>
      <div className="flex flex-row">
        <div className="flex items-center w-64 mb-4">
          <span className="flex items-center justify-center px-3 py-2 text-gray-700 border border-r-0 border-gray-200 rounded-l shadow-sm bg-gray100">
            <CalendarIcon />
          </span>
          <select
            className="w-full rounded-none rounded-r form-select"
            onChange={handleSelectTime}
            value={timeAgo}
          >
            {TIME_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center w-64 mb-4 ml-4">
          <select
            className="w-full rounded-none rounded-r form-select"
            onChange={handleShipmentTypeSelect}
            value={shipmentType}
          >
            {SHIPMENT_TYPES.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <StatsContainer className="py-8">
        {stats.map((item) => {
          const {
            getMetric,
            label,
            type,
            id: itemId,
            info,
            maxRating,
            params,
          } = item;

          return (
            <MetricCard
              getMetric={getMetric}
              label={label}
              type={type}
              id={itemId}
              key={itemId}
              info={info}
              maxRating={maxRating}
              params={params}
            />
          );
        })}
      </StatsContainer>
      {/* Hiding this until we get time series data for Cargomatic and Shipper metrics
      <div className="mt-8">
        <CargomaticGraph
          id={id}
          timeAgo={timeAgo}
          setDashboardTime={setTimeAgo}
          shipmentType={shipmentType}
          dateFilters={getSimpleFilters()}
        />
      </div> */}
      <div className="mt-16">
        <div className="flex flex-wrap -mx-6">
          <div className="lg:w-1/2 w-full px-6">
            <h3 className="my-0 mb-4">Performance By Carrier</h3>
            <MetricsList
              type="Carrier"
              id={id}
              shipmentType={shipmentType}
              dateFilters={getSimpleFilters()}
            />
          </div>
          {displayShipperMetrics && (
            <div className="lg:w-1/2 w-full px-6">
              <h3 className="my-0 mb-4">Performance By Shipper</h3>
              <MetricsList
                type="Shipper"
                shipmentType={shipmentType}
                dateFilters={getSimpleFilters()}
              />
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

CargomaticMetricsDashboard.propTypes = {
  id: PropTypes.string,
};

function getStatConfigs(id, response, timeAgo, shipmentType /* dateFilters */) {
  return [
    {
      getMetric: async () => {
        const value = response.overallStars;

        if (value) {
          return { value: (value/5) * 100 };
        }
        return { value: 0 };
      },
      label: 'Overall',
      type: 'percent',
      info: 'Weighted average of 6 core metrics: on time, stop completion timeliness, POD timeliness, pod quality, completion rate, and app usage.',
      id: 'overall',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.onTimePercentage;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        // if (deltaScore) {
        //   data.delta = deltaScore;
        // }
        return data;
      },
      label: 'On Time',
      type: 'percent',
      info: 'Percentage or pickup and delivery stops completed on time.',
      id: 'on_time',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.stopCompletionTimeliness;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }
        return data;
      },
      label: 'Stop Completion Timeliness',
      type: 'percent',
      info: 'Completion of pickup and delivery stops within an acceptable time frame.',
      id: 'stop_completion_timeliness',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.podTimeliness;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }

        return data;
      },
      label: 'POD Timeliness',
      type: 'percent',
      info: 'Submission of PODs within an acceptable time frame.',
      id: 'pod_timelines',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.podQuality;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }

        return data;
      },
      label: 'POD Quality',
      type: 'percent',
      info: 'Percentage of valid PODs.',
      id: 'pod_quality',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.completionRate;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }

        return data;
      },
      label: 'Completion Rate',
      type: 'percent',
      info: 'Completed jobs divided by total accepted jobs.',
      id: 'completion_rate',
      params: [id, timeAgo, shipmentType],
    },
    {
      getMetric: async () => {
        const value = response.appUsage;
        const data = { value: 0 };
        if (value) {
          data.value = value;
        }

        return data;
      },
      label: 'App Usage',
      type: 'percent',
      info: 'Pickup and delivery stops completed by Carrier or Driver divided by all stops.',
      id: 'app_usage',
      params: [id, timeAgo, shipmentType],
    },
  ];
}

export default CargomaticMetricsDashboard;
